import { Co2MaterialSettingType, ModelCommandResponse } from "@api/apiClient";
import { useUpdateCo2MaterialAssignSettingsItem } from "@api/co2MaterialAssignSettings";
import { useCo2MaterialTypes } from "@api/co2MaterialTypes";
import { useDeleteModelCommand, useModelCommand } from "@api/modelCommand";
import DataGrid from "@components/DataGrid/DataGrid";
import i18n from "@i18n/i18n";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { Box, Snackbar, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridDeleteIcon, GridRowId, GridRowParams, GridRowSelectionModel, useGridApiRef } from "@mui/x-data-grid-premium";
import { useConfirm } from "material-ui-confirm";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";

export interface NonModelledElementGridProps {
  mode?: Co2MaterialSettingType;
  onEdit: (commandObj: ModelCommandResponse) => void;
}

const NonModelledElementsGrid = observer((props: NonModelledElementGridProps) => {
  const [command] = useState("AddNonModelledElement"); // TODO: create type for command
  const [rows, setRows] = useState<ModelCommandResponse[]>([]);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const [openDeleteToast, setOpenDeleteToast] = useState(false);
  const commands = useModelCommand(command);
  const deleteCommand = useDeleteModelCommand();
  const co2MaterialTypes = useCo2MaterialTypes();
  const updateRebarEstimation = useUpdateCo2MaterialAssignSettingsItem();
  const apiRef = useGridApiRef();
  const confirm = useConfirm();

  useEffect(() => {
    const newRows =
      (commands.data?.map((row: any, index: number) => ({
        ...row,
        id: index,
        co2MaterialType: {
          co2MaterialTypeId: JSON.parse(row.arguments).co2MaterialTypeId,
          name:
            JSON.parse(row.arguments).co2MaterialTypeId == undefined
              ? ""
              : co2MaterialTypes.data?.find((type: any) => type.co2MaterialTypeId.toLocaleLowerCase() === JSON.parse(row.arguments).co2MaterialTypeId.toLocaleLowerCase())?.name,
        },
        co2MaterialTypeId: JSON.parse(row.arguments).co2MaterialTypeId,
        lowerLimit: JSON.parse(row.arguments).lowerLimit,
        higherLimit: JSON.parse(row.arguments).higherLimit,
        lastUpdatedTime:row.lastUpdatedTime
      })) as ModelCommandResponse[]) || [];
    setRows(newRows);
  }, [commands.data, co2MaterialTypes.data]);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: i18n.t("non-model-element.non-model-element-grid.columns.name"),
      editable: false,
      flex: 1,
    },
    {
      field: "description",
      headerName: i18n.t("non-model-element.non-model-element-grid.columns.description"),
      editable: false,
      flex: 1,
    },
    {
      field: "arguments",
      headerName: i18n.t("non-model-element.non-model-element-grid.columns.volume"),
      type: "string",
      align: "left",
      headerAlign: "left",
      flex: 1,
      valueGetter: (value: any) => {
        if (value === undefined) {
          return "";
        } else {
          const newValue = JSON.parse(value).value;
          return newValue;
        }
      },
      resizable: true,
      width: 150,
    },
    {
      field: "lowerLimit",
      headerName: i18n.t("non-model-element.non-model-element-grid.columns.lowerlimit"),
      type: "string",
      align: "left",
      headerAlign: "left",
      flex: 1,
      resizable: true,
      width: 150,
    },

    {
      field: "higherLimit",
      headerName: i18n.t("non-model-element.non-model-element-grid.columns.higherlimit"),
      type: "string",
      align: "left",
      headerAlign: "left",
      flex: 1,
      resizable: true,
      width: 150,
    },
    {
      field: "co2MaterialType",
      headerName: i18n.t("non-model-element.non-model-element-grid.columns.ec-material"),
      editable: true,
      flex: 1,
      valueGetter: (value: any) => {
        if (value === undefined) {
          return "";
        } else {
          const newValue = value.name;
          return newValue;
        }
      },
    },
    {
      field: "profileName",
      headerName: i18n.t("non-model-element.non-model-element-grid.columns.profile"),
      editable: false,
      flex: 1,
      renderCell: (params) => {
        const profileName = params.value;
        if (profileName) {
          try {
            return <Box sx={{ wordBreak: "break-word", maxWidth: 200 }}>{profileName}</Box>;
          } catch (error) {
            console.error("Error converting profile to string:", error);
            return "Error";
          }
        }
        return "";
      },
    },
    {
      field: "lastUpdatedTimeinLocalTimeZone",
      headerName: i18n.t("non-model-element.non-model-element-grid.columns.last-updated-time"),
      editable: false,
      flex: 1,
      type: "dateTime", // Helps with sorting/filtering
      valueFormatter: (params: any) => {
        if (!params) return "";
        const date = new Date(params);
        return date.toLocaleString("en-GB", { timeZone: "UTC" }); // or use .toLocaleDateString()
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("non-model-element.non-model-element-grid.columns.actions"),
      resizable: false,
      width: 100,
      getActions: (params: GridRowParams) => {
        return [
          <GridActionsCellItem
            key={`reset-${params.id}`}
            icon={
              <Tooltip title={i18n.t("non-model-element.non-model-element-grid.columns.edit-button-tooltip")}>
                <ModeEditIcon />
              </Tooltip>
            }
            label={i18n.t("non-model-element.non-model-element-grid.columns.edit-button-tooltip")}
            onClick={() => {
              EditCommand(params.id as string);
            }}
          />,
          <GridActionsCellItem
            key={`reset-${params.id}`}
            icon={
              <Tooltip title={i18n.t("non-model-element.non-model-element-grid.columns.delete-button-tooltip")}>
                <GridDeleteIcon />
              </Tooltip>
            }
            label={i18n.t("non-model-element.non-model-element-grid.columns.delete-button-tooltip")}
            onClick={() => {
              handleDeleteClick(params.id as string);
            }}
          />,
        ];
      },
    },
  ];

  function EditCommand(id: string) {
    props.onEdit(rows[id as any] as ModelCommandResponse);
  }

  const handleDeleteClick = async (id: GridRowId) => {
    const { confirmed } = await confirm({
      title: i18n.t("non-model-element.non-model-element-grid.delete-dialog.header"),
      description: i18n.t("non-model-element.non-model-element-grid.delete-dialog.title"),
      confirmationText: i18n.t("dialog.yes"),
      cancellationText: i18n.t("dialog.cancel"),
      dialogProps: {
        maxWidth: "xs",
      },
      confirmationButtonProps: {
        variant: "contained",
        color: "error",
      },
      cancellationButtonProps: {
        variant: "outlined",
      },
    });

    if (confirmed) {
      const updatedRow = rows.find((row: any) => row.id === id);
      if (updatedRow?.commandId) {
        await deleteCommand.mutateAsync(updatedRow.commandId as any);
        setOpenDeleteToast(true);
      }
    }
  };

  return (
    <>
      <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
        <DataGrid
          loading={commands.isFetching || updateRebarEstimation.isPending}
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          disableColumnMenu
          hideFooter={false}
          initialState={{
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
          //checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          sx={{ height: `${(height || 400) - 16}px` }}
        />
      </Box>
      <Snackbar
        open={openDeleteToast}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        message={i18n.t("non-model-element.non-model-element-grid.deleted-success")}
        onClose={() => setOpenDeleteToast(false)}
      />
    </>
  );
});

export default NonModelledElementsGrid;
